import type { ServiceDetail } from "./types";

export const SVILUPPO_WEB_CRM: ServiceDetail = {
  slug: "sviluppo-web-crm",
  category: "digital",
  name: "Sviluppo Web & CRM",
  hero: {
    eyebrow: "Sviluppo Web & CRM",
    title: "Piattaforme su misura: sito, portale e CRM in un'unica regia.",
    subtitle:
      "Progettiamo e sviluppiamo sito istituzionale, portale clienti e CRM integrato come un solo sistema, con automazioni che riducono tempi commerciali e operativi. Niente moduli scollegati, una sola fonte di verità per il tuo team. Sede a Roma, certificati ISO 9001:2015.",
    bullets: [
      "Sito corporate, e-commerce e portali B2B su misura",
      "CRM custom o configurato (HubSpot, Pipedrive, Zoho)",
      "Integrazioni con fatturazione, marketing, ERP e calendario",
      "Automazioni email/Slack/WhatsApp lungo tutta la pipeline",
    ],
  },
  problem: [
    "La maggior parte delle PMI ha un sito vetrina che non genera richieste qualificate, costruito anni fa su template generici, lento da aggiornare e privo di un sistema di tracciamento serio. Quando arrivano contatti, finiscono in caselle email che nessuno presidia in modo strutturato.",
    "I dati dei clienti — anagrafiche, ordini, comunicazioni, preventivi — sono sparsi tra Excel locali, email personali, WhatsApp e cartelle condivise. Quando un commerciale cambia ruolo o lascia l'azienda, lo storico evapora con lui.",
    "I CRM \"chiavi in mano\" risultano spesso troppo rigidi rispetto al processo reale: il team non li compila, il founder non si fida dei numeri e tutto torna a girare nelle email. Le integrazioni tra CRM, gestionale e tool di marketing vengono fatte a mano, copia-incolla settimanale.",
  ],
  approach: [
    "Partiamo dall'analisi del processo commerciale e operativo reale: come arrivano i lead, chi li gestisce, quali stati attraversa una trattativa, dove si perdono dati. Mappiamo flussi e ruoli prima di toccare una riga di codice.",
    "Disegniamo design e architettura insieme: wireframe del sito, struttura del CRM, flusso delle automazioni, modello dati condiviso. Il founder vede l'intera piattaforma su carta prima dello sviluppo.",
    "Lavoriamo in sviluppo iterativo a sprint da 2 settimane: a fine sprint c'è sempre qualcosa di funzionante da testare. Niente progetti \"big bang\" che si vedono solo dopo 6 mesi.",
    "Chiudiamo con training del team operativo, documentazione scritta in italiano e un periodo di affiancamento post go-live in cui rispondiamo dubbi, sistemiamo bug e raccogliamo le prime evolutive.",
  ],
  deliverables: [
    {
      title: "Sito web responsive",
      detail:
        "Sito istituzionale o e-commerce con CMS leggero, obiettivo Lighthouse 90+ mobile, attenzione ai requisiti WCAG AA e SEO tecnico curato.",
    },
    {
      title: "Area riservata clienti",
      detail:
        "Portale B2B con login, gestione ordini, documenti, ticket di assistenza e reportistica personalizzata per ruolo.",
    },
    {
      title: "CRM custom o integrato",
      detail:
        "Sviluppo di un CRM su misura oppure configurazione completa di HubSpot, Pipedrive o Zoho con pipeline, ruoli e automazioni.",
    },
    {
      title: "Automazioni e notifiche",
      detail:
        "Workflow automatici via email, Slack o WhatsApp per lead nuovi, follow-up, scadenze offerte e ingaggio dei commerciali.",
    },
    {
      title: "Documentazione operativa",
      detail:
        "Manuale utente in italiano, schemi di architettura, accessi, runbook deploy e checklist per onboarding di nuovi membri del team.",
    },
    {
      title: "Training e affiancamento",
      detail:
        "Sessioni di formazione per il team commerciale e operativo, video registrati e 30 giorni di supporto post go-live inclusi.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Settimana 1-2 — Kickoff & discovery",
      text: "AB MARK conduce workshop con commerciale, operations e founder, mappa flussi e produce wireframe + modello dati. Tu fornisci accesso ai sistemi attuali e individui i decisori. Output: documento di scope condiviso.",
    },
    {
      step: "02",
      title: "Settimana 3-8 — MVP",
      text: "AB MARK sviluppa la prima versione funzionante di sito + CRM + integrazioni critiche, in sprint da 2 settimane con demo a fine sprint. Tu valutate ogni demo e dai priorità per lo sprint successivo.",
    },
    {
      step: "03",
      title: "Settimana 9-12 — Iterazioni",
      text: "AB MARK rifinisce UX, automazioni e integrazioni secondarie sulla base dei feedback. Tu organizzi una sessione di test con utenti reali del team prima del go-live.",
    },
    {
      step: "04",
      title: "Go-live & supporto",
      text: "AB MARK gestisce migrazione dati, deploy in produzione, training del team e 30 giorni di affiancamento. Successivamente subentra il canone mensile di evolutiva e manutenzione.",
    },
  ],
  faq: [
    {
      q: "Quali tecnologie usate per i progetti custom?",
      a: "Lo stack standard è React + TypeScript per il frontend, Node.js / Express per il backend, PostgreSQL come database e deploy su infrastruttura cloud europea (server in UE per garantire compliance GDPR). Per CRM \"a configurazione\" lavoriamo nativamente su HubSpot, Pipedrive e Zoho.",
    },
    {
      q: "Come funziona la manutenzione dopo il lancio?",
      a: "Includiamo 30 giorni di supporto gratuito post go-live. Successivamente proponiamo un canone mensile (a partire da 600 €/mese) che copre monitoraggio, aggiornamenti di sicurezza, piccole evolutive e ore di assistenza del team commerciale. Senza vincoli pluriennali.",
    },
    {
      q: "A chi appartiene il codice e i dati alla fine del progetto?",
      a: "Il codice sorgente e i dati sono di proprietà del cliente al 100%, consegnati su un repository Git intestato all'azienda. Non blocchiamo i clienti su tecnologie proprietarie: se un domani volete cambiare fornitore, il codice è leggibile e documentato per qualsiasi sviluppatore senior.",
    },
    {
      q: "Si integra con i software che usiamo già (gestionale, fatturazione, marketing)?",
      a: "Sì, abbiamo già realizzato integrazioni con i principali gestionali italiani (TeamSystem, Zucchetti, Danea), piattaforme di fatturazione elettronica, Mailchimp/Brevo/ActiveCampaign per l'email marketing e Google Workspace / Microsoft 365 per calendario e documenti. Per software custom valutiamo lo stack disponibile e usiamo API o webhook.",
    },
    {
      q: "Quali sono i tempi medi per un progetto?",
      a: "Sito istituzionale professionale: 4-6 settimane. Sito + CRM configurato (HubSpot/Pipedrive): 6-10 settimane. Portale clienti + CRM custom + integrazioni: 12-16 settimane. I progetti vengono fasizzati per andare in produzione presto e poi crescere a sprint successivi.",
    },
    {
      q: "Quanto costa indicativamente un progetto?",
      a: "Sito istituzionale custom: da 6.500 €. Sito + CRM configurato: da 12.000 €. Portale + CRM custom + integrazioni: da 25.000 € one-shot più canone mensile. Forniamo sempre un preventivo dettagliato dopo la fase di discovery iniziale, gratuita per progetti sopra i 6.500 €.",
    },
  ],
  seo: {
    title: "Sviluppo Siti Web e CRM Roma | PMI e B2B | AB MARK",
    description:
      "Sviluppo siti web a Roma e CRM personalizzato per PMI italiane: integrazione CRM CMS PMI, fatturazione e marketing, training del team e supporto post lancio.",
  },
};
